import type { AirlineDeductions, DeductionSource, DepartureTiming, SearchCheck, SearchReading } from './types'

/**
 * The penalty the fare search offered at booking, held against a quote read from the fare rules. It
 * never changes the deductions: agreeing can raise the score, differing raises a warning, and with no
 * figure at all it is shown for the agent to confirm. 277 landed refunds put it right about two times
 * in three (NZ 8 of 8), so it is a second opinion, not a source.
 */
export type SearchOpinion =
  | { verdict: 'agrees'; note: string }
  | { verdict: 'differs'; warning: string }
  | { verdict: 'suggests'; note: string }

/** Sources read from the filed rules, which a second reading can corroborate. Sabre's own figures need no help. */
const FARE_RULE_SOURCES = new Set<DeductionSource>(['PARSED_FARE_RULES', 'STRUCTURED_RULES', 'NON_REFUNDABLE_FARE'])

const agreesWith = (quoted: number, offered: number) => Math.abs(quoted - offered) <= Math.max(1, 0.02 * offered)

export interface SearchOpinionInput {
  searchCheck?: SearchCheck
  deductions: AirlineDeductions
  timing: DepartureTiming
  cancellingPax: number
  currency: string
}

export function searchOpinion({
  searchCheck,
  deductions,
  timing,
  cancellingPax,
  currency,
}: SearchOpinionInput): SearchOpinion | undefined {
  if (!searchCheck || searchCheck.currency.toUpperCase() !== currency.toUpperCase()) return undefined
  const reading = timing === 'AFTER_DEPARTURE' ? searchCheck.afterDeparture : searchCheck.beforeDeparture
  if (!reading || searchCheck.passengers <= 0) return undefined

  const share = Math.min(1, cancellingPax / searchCheck.passengers)
  const offered = { ...reading, penalty: Math.round(reading.penalty * share * 100) / 100 }
  const described = describe(offered, currency)

  if (deductions.requiresManualDeductions) {
    return {
      verdict: 'suggests',
      note: `At booking the fare search offered ${described} — confirm it with the airline before entering it.`,
    }
  }
  if (!FARE_RULE_SOURCES.has(deductions.source)) return undefined

  const agrees =
    deductions.source === 'NON_REFUNDABLE_FARE'
      ? !offered.cancellationAllowed
      : offered.cancellationAllowed && agreesWith(deductions.penalty, offered.penalty)

  return agrees
    ? { verdict: 'agrees', note: `The fare search at booking offered the same: ${described}.` }
    : {
        verdict: 'differs',
        warning:
          `search-penalty-differs: the fare search at booking offered ${described}, ` +
          `the quote deducts a ${currency} ${deductions.penalty.toFixed(2)} penalty`,
      }
}

function describe({ cancellationAllowed, penalty }: SearchReading, currency: string): string {
  if (!cancellationAllowed) return 'a non-refundable fare'
  return penalty === 0 ? 'a refund with no fee' : `a refund with a ${currency} ${penalty.toFixed(2)} fee`
}
